import * as Sentry from "@sentry/react";
import { User } from "@supabase/supabase-js";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AlertCircle } from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { CreatorAudioEngine } from "./components/CreatorAudioEngine";
import Header from "./components/Header";
import HeaderOscilloscope from "./components/HeaderOscilloscope";
import NowPlayingToast from "./components/NowPlayingToast";
import PlayerBar from "./components/PlayerBar";
import ScrollProgress from "./components/ScrollProgress";
import { SURPRISE_GENRES } from "./constants/categories";
import { AppContext } from "./contexts/AppContext";
import { useFavicon } from "./hooks/useFavicon";
import { useFavorites } from "./hooks/useFavorites";
import { useFollowedPodcasts } from "./hooks/useFollowedPodcasts";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useListeningHistory } from "./hooks/useListeningHistory";
import { useLiveDJ } from "./hooks/useLiveDJ";
import { usePlayer } from "./hooks/usePlayer";

import { useStationData } from "./hooks/useStationData";
import { useSwipeNavigation } from "./hooks/useSwipeNavigation";
import { SEED_PODS } from "./services/data/seedPods";
import { generateSpeech, chatWithDJ } from "./services/genaiService";
import {
  getCountries,
  searchStations,
  getTopStations,
} from "./services/radioService";
import { getUserRegion } from "./services/regionService";
import { supabase } from "./services/supabase";
import {
  Station,
  DJPersona,
  ViewMode,
  ChatMessage,
  LayoutMode,
  Podcast,
} from "./types";
import { makeStationSlug } from "./utils/slug";

// --- Lazy Load Routes ---
const HomeRoute = React.lazy(() =>
  import("./components/routes/HomeRoute").then((module) => ({
    default: module.HomeRoute,
  })),
);
const RadioRoute = React.lazy(() =>
  import("./components/routes/RadioRoute").then((module) => ({
    default: module.RadioRoute,
  })),
);
const PodcastRoute = React.lazy(() =>
  import("./components/routes/PodcastRoute").then((module) => ({
    default: module.PodcastRoute,
  })),
);
const LibraryRoute = React.lazy(() =>
  import("./components/routes/LibraryRoute").then((module) => ({
    default: module.LibraryRoute,
  })),
);
const SearchRoute = React.lazy(() =>
  import("./components/routes/SearchRoute").then((module) => ({
    default: module.SearchRoute,
  })),
);

// Lazy Load All Heavy Components for better FCP
const ZenMode = React.lazy(() => import("./components/ZenMode"));
const Sidebar = React.lazy(() => import("./components/Sidebar"));
const ProfileMenu = React.lazy(() => import("./components/ProfileMenu"));
const ApiDocs = React.lazy(() => import("./components/ApiDocs"));
const LoginView = React.lazy(() => import("./components/Auth/LoginView"));
const RegisterView = React.lazy(() => import("./components/Auth/RegisterView"));
const AuthCallback = React.lazy(() => import("./components/Auth/AuthCallback"));
const AccountView = React.lazy(() => import("./components/Auth/AccountView"));
const AuthGate = React.lazy(() => import("./components/Auth/AuthGate"));
const LiveStationView = React.lazy(
  () => import("./components/LiveStationView"),
);
const LegalView = React.lazy(() => import("./components/LegalView"));
const NotFoundView = React.lazy(() => import("./components/NotFoundView"));
const MoodView = React.lazy(() => import("./components/MoodView"));
const FrontDoor = React.lazy(() => import("./components/FrontDoor"));
const CreatorsChoicePage = React.lazy(
  () => import("./components/CreatorsChoicePage"),
);
const HotkeyMenu = React.lazy(() => import("./components/HotkeyMenu"));
const Toast = React.lazy(() => import("./components/Toast"));

// Additional lazy-loaded components for FCP optimization
const Footer = React.lazy(() => import("./components/Footer"));
const FilterBar = React.lazy(() =>
  import("./components/FilterBar").then((m) => ({ default: m.FilterBar })),
);
const CookieConsent = React.lazy(() => import("./components/CookieConsent"));
const MobileSearchView = React.lazy(
  () => import("./components/MobileSearchView"),
);
const FilterBottomSheet = React.lazy(
  () => import("./components/FilterBottomSheet"),
);

function App() {
  // --- Core State ---
  const [user, setUser] = useState<User | null>(null);
  const [currentStation, setCurrentStation] = useState<Station | null>(null);
  const [isPlayerExpanded, setIsPlayerExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const [suppressMiniPlayer, setSuppressMiniPlayer] = useState(false);


  // App Modes
  const location = useLocation();
  const navigate = useNavigate();

  // Derive viewMode from URL
  const viewMode: ViewMode = React.useMemo(() => {
    const path = location.pathname;
    if (path === "/") return "landing";
    if (path === "/radio" || path === "/podcasts") return "home";
    if (path === "/library") return "library";
    if (path === "/search") return "search";
    if (path === "/search") return "search";
    if (path.startsWith("/radio/moods/")) return "home"; // Treat mood playlist as home/list
    if (path.startsWith("/radio/")) return "station";
    if (path.startsWith("/podcasts/")) return "podcast-details";
    if (path.startsWith("/pods/")) return "podcast-details";
    if (path.startsWith("/pods")) return "home"; // Podcast home
    return "home";
  }, [location.pathname]);

  const activeNav = React.useMemo<
    "landing" | "radio" | "podcasts" | "creators"
  >(() => {
    const path = location.pathname;
    if (path.startsWith("/podcasts") || path.startsWith("/pods"))
      return "podcasts";
    if (path === "/") return "landing";
    if (path.startsWith("/radio")) return "radio";
    if (path.startsWith("/creators-choice")) return "creators";
    return "landing";
  }, [location.pathname]);

  //  const [viewMode, setViewMode] = useState<ViewMode>("landing");
  // Keep for back button logic?

  const [layoutMode, setLayoutMode] = useState<LayoutMode>("carousel"); // Default to Carousel
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false); // NEW: Track if any filter dropdown is open
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isFilterBottomSheetOpen, setIsFilterBottomSheetOpen] = useState(false); // Mobile filter sheet

  // Data
  const [countries, setCountries] = useState<
    { name: string; stationcount: number }[]
  >([]);
  const [userRegion, setUserRegion] = useState<string>("Worldwide");
  const [sortBy, setSortBy] = useState<
    "popular" | "trending" | "bitrate" | "name" | "latest_episode"
  >("popular");
  const [lastRadioCategory, setLastRadioCategory] = useState<string>("");
  const [lastPodcastCategory, setLastPodcastCategory] = useState<string>("");
  const [lastRadioCountry, setLastRadioCountry] = useState<string>("All");
  const [lastPodcastCountry, setLastPodcastCountry] = useState<string>("All");

  // Toggles
  const [isZenMode, setIsZenMode] = useState(false);
  const [showEq, setShowEq] = useState(false);
  const [eqGains, setEqGains] = useState<number[]>([0, 0, 0, 0, 0]);
  const [isAutoAnnounceEnabled, setIsAutoAnnounceEnabled] = useState(false);
  const [hasRated, _setHasRated] = useState(false);
  const [_hasSurprised, setHasSurprised] = useState(false);
  const [currentContext, setCurrentContext] = useState<{
    type: "grid" | "carousel" | "search" | "favorites" | "related" | "history";
    items: Station[];
    index: number;
  } | null>(null);

  // Shuffle state
  const [isShuffling, setIsShuffling] = useState(false);
  const [originalContextItems, setOriginalContextItems] = useState<
    Station[] | null
  >(null);

  // Context
  const [weatherContext, setWeatherContext] = useState<string>("");
  const [locationContext, setLocationContext] = useState<string>("");

  // Personalization
  const [djPersona, setDjPersona] = useState<DJPersona>("upbeat");
  const { favorites, toggleFavorite: baseTogleFavorite } = useFavorites();
  const {
    followPodcast,
    isFollowed: _isFollowed,
    notifications,
    unreadCount,
    markAllRead,
    clearNotifications,
    removeNotification,
  } = useFollowedPodcasts();
  const [hiddenStationIds] = useState<string[]>([]);
  const { addOrUpdateItem: addHistoryItem } = useListeningHistory();

  // Customization State (Lifted from HomeView)
  const [customizeSection, setCustomizeSection] = useState<
    "radio" | "podcasts" | null
  >(null);

  // Enhanced toggleFavorite that also triggers notifications for podcasts
  const toggleFavorite = useCallback(
    (item: Station | Podcast) => {
      // Check for podcast specific properties safely
      const pItem = item as {
        episodes?: unknown[];
        latest_episode?: unknown;
        episodeCount?: number;
      };
      const isPodcastItem = Boolean(
        pItem.episodes || pItem.latest_episode || pItem.episodeCount,
      );

      const itemId = (item as { stationuuid?: string }).stationuuid || item.id;
      const wasFavorite = favorites.some(
        (f) => ((f as { stationuuid?: string }).stationuuid || f.id) === itemId,
      );

      // Call the base toggle
      baseTogleFavorite(item);

      // If it's a podcast and we're adding (not removing), trigger notification
      if (isPodcastItem && !wasFavorite) {
        followPodcast({
          id: itemId,
          name: (item as Podcast).title || (item as Station).name || "Podcast",
          logoUrl:
            (item as Station).logoUrl ||
            (item as Podcast).imageUrl ||
            (item as any).thumbnail ||
            (item as any).favicon,
        });
      }
    },
    [baseTogleFavorite, favorites, followPodcast],
  );

  // Theme
  const [themeMode, setThemeMode] = useState<"dark" | "light">(() => {
    if (typeof window === "undefined") return "dark";
    const saved = localStorage.getItem("theme");
    return saved === "light" ? "light" : "dark";
  });
  const [accentColor, setAccentColor] = useState<string>("#22d3ee");

  // AI & Chat
  const [textInput, setTextInput] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [sleepTimer, setSleepTimer] = useState<number | null>(null);

  // Refs
  const sleepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const touchStartRef = useRef(0);
  const [pullToRefreshDist, setPullToRefreshDist] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Prevents effect loops
  const prevStationIdRef = useRef<string | null>(null);

  // --- GATE LOGIC ---
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [gatePendingAction, setGatePendingAction] = useState<
    (() => void) | null
  >(null);
  const triggerGate = useCallback(
    (onContinue: () => void) => {
      // 0. If user is logged in, never gate
      if (user) {
        onContinue();
        return;
      }

      // 1. Check "Never Show Again" (Guest Mode Forever)
      if (localStorage.getItem("auth_gate_never_show")) {
        onContinue();
        return;
      }

      // 2. Check Cooldown (if they just dismissed it recently)
      const dismissedAt = localStorage.getItem("auth_gate_dismissed_at");
      if (dismissedAt) {
        const daysSinceDismissal =
          (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissal < 7) {
          // Still in cooldown -> Bypass gate
          onContinue();
          return;
        }
      }

      // 3. Show Gate
      setGatePendingAction(() => onContinue);
      setIsGateOpen(true);
    },
    [user],
  );

  const handleGateGuest = () => {
    // User clicked "Continue as Guest" - NEVER SHOW AGAIN
    localStorage.setItem("auth_gate_never_show", "true");
    
    // Also update cooldown just in case
    localStorage.setItem("auth_gate_dismissed_at", Date.now().toString());
    
    // Close Gate and RUN action
    setIsGateOpen(false);
    if (gatePendingAction) {
      gatePendingAction();
      setGatePendingAction(null);
    }
  };

  const handleGateClose = () => {
    // User clicked "X" or "Background"
    // 1. Set Cooldown (so we don't spam them immediately if they just want to look around)
    localStorage.setItem("auth_gate_dismissed_at", Date.now().toString());
    // 2. Close Gate
    setIsGateOpen(false);
    // 3. CANCEL the pending action (they chose not to proceed)
    setGatePendingAction(null);
  };

  // --- HOOKS ---

  // 1. Data Logic Hook
  const {
    stations,
    setStations,
    loadingStations,
    setLoadingStations,
    activeType,
    setActiveType,
    selectedCountry,
    setSelectedCountry,
    selectedTheme,
    setSelectedTheme,
    stationSearchQuery: typedSearchQuery,
    setStationSearchQuery: setTypedSearchQuery,
    locationError,
    loadFilteredStations,
    handleNearMe,
    handleManualSearch: originalHandleManualSearch,
    podcastCounts,
  } = useStationData(countries);

  // --- Search State Separation ---
  const [_confirmedSearchQuery, setConfirmedSearchQuery] = useState("");
  const [liveSearchResults, setLiveSearchResults] = useState<Station[]>([]);


  const HEADER_HEIGHT_MOBILE = 64;

  // Sync node to ref for other usages (scrollTo)


  // 2. Player Logic Hook
  const handleAddToHistory = useCallback(
    (s: Station) => {
      const isPodcast = s.provider === "podcast" || s.id.startsWith("pod-");

      if (isPodcast) {
        addHistoryItem({
          id: s.streamUrl || s.id,
          type: "podcast",
          title: s.lastEpisodeTitle || s.name,
          subtitle: s.name,
          source: s.id,
          imageUrl: s.logoUrl,
          country: s.country,
          region: s.state,
          genres: Array.isArray(s.tags) ? s.tags : undefined,
        });
        return;
      }

      addHistoryItem({
        id: s.id || s.streamUrl || s.name,
        type: "radio",
        title: s.name,
        subtitle: s.country || s.countryCode,
        source: s.provider || "radio",
        imageUrl: s.logoUrl,
        country: s.country || s.countryCode,
        region: s.state,
        genres: Array.isArray(s.tags)
          ? s.tags
          : typeof (s as { tags?: string | string[] }).tags === "string"
            ? [(s as { tags?: string | string[] }).tags as string]
            : undefined,
      });
    },
    [addHistoryItem],
  );

  const {
    audioRef,
    isPlaying,
    setIsPlaying,
    volume,
    setVolume,
    currentTime,
    duration,
    playStation,
    togglePlay,
    handleSeek,
    handleTimeUpdate,
    handleSkip,
    lastError,
    setLastError,
    isBuffering,
    playbackSpeed,
    cyclePlaybackSpeed,
  } = usePlayer(hasRated, djPersona, handleAddToHistory);

  const handleViewChange = useCallback(
    (newMode: ViewMode, params?: string) => {
      console.log(`[App] handleViewChange: ${newMode}`, params);

      if (newMode === "landing") navigate("/");
      else if (newMode === "home") {
        if (activeType === "pods") navigate("/podcasts");
        else navigate("/radio");
      } else if (newMode === "library") navigate("/library");
      else if (newMode === "search") navigate("/search");
      else if (newMode === "station" && params)
        navigate(`/radio/${params}`); // Params should be slug
      else if (newMode === "podcast-details" && params)
        navigate(`/podcasts/${params}`); // Params should be ID
      else if (newMode === "account") navigate("/account");
      else if (newMode === "login") navigate("/login");
      else if (newMode === "favorites") navigate("/library?tab=favorites");
      else if (newMode === "history") navigate("/library?tab=history");

      // Handle Params logic if needed (e.g. setting selectedPodcast)
      // But Routes should handle fetching.

      // Scroll to top
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [navigate, activeType],
  );

  const handleExitSearch = useCallback(() => {
    setTypedSearchQuery("");
    setConfirmedSearchQuery("");
    setLiveSearchResults([]);
    setIsSearchOpen(false);
    handleViewChange("home");
  }, [
    handleViewChange,
    setTypedSearchQuery,
    setConfirmedSearchQuery,
    setLiveSearchResults,
    setIsSearchOpen,
  ]);

  // Animated Favicon - spins vinyl when playing
  useFavicon(isPlaying);

  // Keyboard Shortcuts
  const { isHotkeyMenuOpen, closeHotkeyMenu } = useKeyboardShortcuts({
    onPlayPause: () => togglePlay(),
    onNext: () => handleSkipStation("next"),
    onPrev: () => handleSkipStation("prev"),
    onSearch: () => setIsSearchOpen(true),
    onShowHotkeys: () => {},
    isEnabled: !isSidebarOpen && !isProfileOpen,
  });

  // Now Playing Toast - shows when station changes
  const [showNowPlayingToast, setShowNowPlayingToast] = useState(false);
  const prevStationNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (currentStation && currentStation.name !== prevStationNameRef.current) {
      if (prevStationNameRef.current !== null) {
        // Only show toast when changing from one station to another
        setShowNowPlayingToast(true);
      }
      prevStationNameRef.current = currentStation.name;
    }
  }, [currentStation]);

  // NEW: Station Info Sheet State
  const playLoggedRef = useRef<{ stationId: string | null; logged: boolean }>({
    stationId: null,
    logged: false,
  });

  // --- CREATOR ENGINE STATE ---
  const [creatorTrack, setCreatorTrack] = useState<{
    title?: string;
    url?: string;
  } | null>(null);
  const [creatorCurrentTime, setCreatorCurrentTime] = useState(0);
  const [creatorDuration, setCreatorDuration] = useState(0);
  const [isCreatorPlaying, setIsCreatorPlaying] = useState(false);

  const isCreatorMode =
    currentStation?.provider === "creator" ||
    currentStation?.provider === "custom";

  // Helper to determine what PlayerBar shows
  const effectiveCurrentStation =
    isCreatorMode && creatorTrack
      ? ({
          ...currentStation, // Base channel info
          name: creatorTrack.track.title, // Song Title
          country: creatorTrack.track.artist, // Artist Name (hijacking country field for display)
          logoUrl: creatorTrack.track.cover || currentStation?.logoUrl, // Song cover or Channel Cover
        } as Station)
      : currentStation;

  const effectiveIsPlaying = isCreatorMode ? isCreatorPlaying : isPlaying;
  const effectiveCurrentTime = isCreatorMode ? creatorCurrentTime : currentTime;
  const effectiveDuration = isCreatorMode ? creatorDuration : duration;

  // Toggle Play Wrapper
  const handleGlobalTogglePlay = () => {
    if (isCreatorMode) {
      setIsCreatorPlaying(!isCreatorPlaying);
    } else {
      togglePlay();
    }
  };

  const handleSkipStation = (direction: "next" | "prev") => {
    const contextItems =
      currentContext?.items && currentContext.items.length > 0
        ? currentContext.items
        : stations; // Fallback to all stations if no context

    if (!currentStation || contextItems.length === 0) return;

    let currentIndex = contextItems.findIndex(
      (s) => s.id === currentStation.id,
    );

    if (currentIndex === -1) currentIndex = 0;

    let nextIndex = 0;
    if (direction === "next") {
      nextIndex = (currentIndex + 1) % contextItems.length;
    } else {
      nextIndex =
        (currentIndex - 1 + contextItems.length) % contextItems.length;
    }

    const nextStation = contextItems[nextIndex];

    // Directly set station and play, bypassing context reset
    setCurrentStation(nextStation);

    if (
      nextStation.provider === "podcast" ||
      nextStation.id.startsWith("pod-")
    ) {
      // Conditional Navigation: Only navigate if user is currently on a detail page ('station' or 'podcast-details')
      if (viewMode === "station" || viewMode === "podcast-details") {
        handleViewChange("podcast-details", nextStation.id);
      }
    } else {
      // Conditional Navigation: Only navigate if user is currently on a detail page ('station' or 'podcast-details')
      if (viewMode === "station" || viewMode === "podcast-details") {
        const slug = makeStationSlug(nextStation);
        handleViewChange("station", slug);
      }

      if (!isPlaying) togglePlay();
    }
  };

  const handleShuffle = () => {
    if (!currentContext || !currentContext.items.length) return;

    if (isShuffling) {
      // Unshuffle: Restore original order
      if (originalContextItems) {
        setCurrentContext((prev) =>
          prev ? { ...prev, items: originalContextItems } : null,
        );
        setOriginalContextItems(null);
      }
      setIsShuffling(false);
    } else {
      // Shuffle
      setOriginalContextItems(currentContext.items);

      // Create shuffled copy
      const shuffled = [...currentContext.items];
      // Fisher-Yates shuffle
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      // Move current station to start of shuffled list so next click goes to a random song
      if (currentStation) {
        const currentIdx = shuffled.findIndex(
          (s) => s.id === currentStation.id,
        );
        if (currentIdx > -1) {
          shuffled.splice(currentIdx, 1);
          shuffled.unshift(currentStation);
        }
      }

      setCurrentContext((prev) => (prev ? { ...prev, items: shuffled } : null));
      setIsShuffling(true);
    }
  };

  // Reset log flag when station changes
  useEffect(() => {
    playLoggedRef.current = {
      stationId: currentStation?.id || null,
      logged: false,
    };
  }, [currentStation?.id]);

  const logStationPlay = useCallback(async (station: Station) => {
    try {
      // Track metric in Sentry
      // Track metric in Sentry
      // Track metric in Sentry
      // Track metric in Sentry
      const sentryAny = Sentry as {
        metrics?: {
          increment?: (
            name: string,
            value: number,
            options: { tags: Record<string, string> },
          ) => void;
        };
      };
      if (
        sentryAny.metrics &&
        typeof sentryAny.metrics.increment === "function"
      ) {
        sentryAny.metrics.increment("station.play", 1, {
          tags: {
            country: station.countryCode,
            provider: station.provider || "radio",
          },
        });
      }

      if (!supabase) return;

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }

      await fetch("/api/stats/station-play", {
        method: "POST",
        headers,
        body: JSON.stringify({
          stationId: station.id,
          countryCode: station.countryCode,
          itemType:
            station.provider === "podcast" || station.id.startsWith("pod-")
              ? "podcast"
              : "station",
        }),
      });
    } catch (err) {
      console.warn("[Stats] Failed to log play:", err);
    }
  }, []);

  // Note: Scroll-aware header logic removed for cleaner UX.
  // FilterBar is now sticky and always accessible.
  // Header scrolls naturally with content.

  // Log play after at least 3 seconds of playback to avoid click-spam
  useEffect(() => {
    if (!currentStation || !isPlaying) return;
    if (currentTime < 3) return;

    const ref = playLoggedRef.current;
    if (ref.stationId !== currentStation.id) {
      playLoggedRef.current = { stationId: currentStation.id, logged: false };
    }
    if (playLoggedRef.current.logged) return;

    playLoggedRef.current.logged = true;
    logStationPlay(currentStation);
  }, [currentTime, isPlaying, currentStation, logStationPlay]);

  // --- Effects ---

  // Initialization
  useEffect(() => {
    const init = async () => {
      // Load saved theme
      const savedTheme = localStorage.getItem("theme") as
        | "dark"
        | "light"
        | null;
      if (savedTheme) setThemeMode(savedTheme);

      const radioCountries = await getCountries();
      const podCountries = new Set(SEED_PODS.map((p) => p.country));
      const existingCountryNames = new Set(radioCountries.map((c) => c.name));
      const mergedCountries = [...radioCountries];
      podCountries.forEach((pc) => {
        if (!existingCountryNames.has(pc))
          mergedCountries.push({ name: pc, stationcount: 0 });
      });
      // Deduplicate mergedCountries by name
      const uniqueCountriesMap = new Map();
      mergedCountries.forEach((c) => {
        if (!uniqueCountriesMap.has(c.name)) {
          uniqueCountriesMap.set(c.name, c);
        }
      });
      const uniqueCountries = Array.from(uniqueCountriesMap.values());

      uniqueCountries.sort((a, b) => a.name.localeCompare(b.name));
      setCountries(uniqueCountries);

      const region = getUserRegion();
      setUserRegion(region);

      // Check for API Docs route
      if (window.location.pathname === "/dev/api-docs") {
        // setViewMode('api-docs'); // Removed setViewMode
      }
    };
    init();

    // Auth Listener
    if (supabase) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
      });

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });

      return () => subscription.unsubscribe();
    }
  }, []);

  // Theme Application - Simplified (accent color only, theme handled by CSS classes)
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--color-accent", accentColor);
    root.style.setProperty("--color-accent-glow", `${accentColor}33`);
  }, [accentColor]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Suppress mini-player when large overlays are open on mobile
    if (
      isMobile &&
      (isSearchOpen || isSidebarOpen || isProfileOpen || isMainMenuOpen)
    ) {
      setSuppressMiniPlayer(true);
    } else {
      setSuppressMiniPlayer(false);
    }
  }, [isMobile, isSearchOpen, isSidebarOpen, isProfileOpen, isMainMenuOpen]);

  // Overlay coordination helpers
  const openProfilePanel = () => {
    setIsMainMenuOpen(false);
    setIsSearchOpen(false);
    setIsProfileOpen(true);
  };

  const toggleProfilePanel = () => {
    if (isProfileOpen) setIsProfileOpen(false);
    else openProfilePanel();
  };

  const openMainMenu = () => {
    setIsProfileOpen(false);
    setIsSearchOpen(false);
    setIsMainMenuOpen(true);
  };

  const closeMainMenu = () => setIsMainMenuOpen(false);

  const toggleSearchOverlay = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false);
    } else {
      setIsMainMenuOpen(false);
      setIsProfileOpen(false);
      setIsSearchOpen(true);
    }
  };

  useEffect(() => {
    const path = location.pathname;
    if (path === "/library" || path === "/search") return;

    if (path.startsWith("/podcasts") || path.startsWith("/pods")) {
      if (activeType !== "pods") setActiveType("pods");
      return;
    }

    if (path === "/" || path === "/radio" || path.startsWith("/radio/")) {
      if (activeType !== "radio") setActiveType("radio");
    }
  }, [location.pathname, activeType, setActiveType]);

  // Auto-minimize/expand player on mobile based on route
  useEffect(() => {
    const path = location.pathname;
    
    // Check if we're on a podcast detail page specifically
    const isPodcastDetail = path.startsWith("/podcasts/") && path.length > 10;
    
    if (isMobile) {
      if (isPodcastDetail) {
        // Auto-minimize when entering podcast detail (but user can still expand manually)
        // Only minimize if we're currently expanded - don't fight the user
        console.log("[App] Entering podcast detail, auto-minimizing player");
        setIsPlayerExpanded(false);
      } else {
        // Auto-expand when leaving podcast detail (going back to main views)
        console.log("[App] Leaving podcast detail, auto-expanding player");
        setIsPlayerExpanded(true);
      }
    }
  }, [location.pathname, isMobile]);

  // Persist theme to localStorage and sync with body class
  useEffect(() => {
    localStorage.setItem("theme", themeMode);
    document.body.classList.remove("theme-dark", "theme-light");
    document.body.classList.add(`theme-${themeMode}`);

    // Standard Tailwind Dark Mode
    if (themeMode === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [themeMode]);

  // Escape key to close search/filters
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isSearchOpen) {
          setIsSearchOpen(false);
        }
        // If in search view, exit to home
        if (viewMode === "search") {
          handleExitSearch();
        }
        // FilterBar handles its own dropdown closing
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isSearchOpen, viewMode]);

  // Handle search overlay exit (Escape key)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSearchOpen) {
        // Assuming isSearchOpen is the correct state for the overlay
        handleExitSearch();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isSearchOpen, handleExitSearch]); // Changed isSearchOverlayOpen to isSearchOpen

  // Sync player state with audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [setIsPlaying, audioRef]);

  useEffect(() => {
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    if (sleepTimer) {
      sleepTimerRef.current = setTimeout(
        () => {
          if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            setSleepTimer(null);
          }
        },
        sleepTimer * 60 * 1000,
      );
    }
    return () => {
      if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    };
  }, [sleepTimer, setIsPlaying, audioRef]);

  useEffect(() => {
    if (currentStation) {
      if (
        currentStation.id !== prevStationIdRef.current ||
        currentStation.provider === "podcast"
      ) {
        playStation(currentStation);
        prevStationIdRef.current = currentStation.id;
      }
    }
  }, [currentStation, playStation]);

  const handleOpenZen = () => {
    if (!currentStation) {
      setLastError("Start a station or podcast first to use Zen Mode.");
      return;
    }
    setIsZenMode(true);
  };

  // --- LIVE SEARCH EFFECT (Overlay Only) ---
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (typedSearchQuery.trim().length > 0) {
        // Only open search input if we're NOT already in Search View
        if (!isSearchOpen && viewMode !== "search") {
          setIsSearchOpen(true);
        }

        // Perform a lightweight search for suggestions
        // We can reuse searchStations but maybe with a smaller limit if desired
        try {
          const results = await searchStations({
            name: typedSearchQuery,
            limit: 10,
          });
          setLiveSearchResults(results);
        } catch (e) {
          console.warn("Live search failed", e);
        }
      } else {
        setLiveSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [typedSearchQuery, isSearchOpen, viewMode]);

  // --- CONFIRMED SEARCH HANDLER ---
  const handleManualSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typedSearchQuery.trim()) return;

    setConfirmedSearchQuery(typedSearchQuery.trim());
    // setViewMode('search'); // Removed setViewMode
    navigate("/search");
    setIsSearchOpen(false); // Close overlay as we move to full view

    // Trigger the main search fetch
    // We can use the original handleManualSearch from hook or call searchStations directly
    // The hook's handleManualSearch updates 'stations' state which is what we want for the grid
    originalHandleManualSearch(e);
  };

  // --- SWIPE NAVIGATION ---
  const swipeHandlers = useSwipeNavigation();

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // 1. Pull-to-Refresh Logic
    if (e.currentTarget.scrollTop === 0)
      touchStartRef.current = e.touches[0].clientY;
    else touchStartRef.current = 0;

    // 2. Swipe Nav Logic
    swipeHandlers.onTouchStart(e);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    // 1. Pull-to-Refresh Logic
    if (touchStartRef.current !== 0) {
      // Only if started at top
      const currentY = e.touches[0].clientY;
      const diff = currentY - touchStartRef.current;
      if (diff > 0 && e.currentTarget.scrollTop === 0) {
        setPullToRefreshDist(Math.min(diff * 0.4, 150));
      } else {
        setPullToRefreshDist(0);
      }
    }

    // 2. Swipe Nav Logic
    swipeHandlers.onTouchMove(e);
  };

  const handleRefresh = async () => {
    setLoadingStations(true);
    try {
      // Refresh based on current view/mode
      if (activeType === "radio" && layoutMode === "carousel") {
        // In carousel mode, we might want to refresh top stations or just re-fetch the current country
        await getTopStations().then(setStations);
      } else {
        // In grid/list or other modes, re-fetch filtered stations
        await loadFilteredStations(activeType, selectedTheme, selectedCountry);
      }

      // Also refresh podcasts if we are in podcast mode
      if (activeType === "pods") {
        // loadFilteredStations handles this for pods too
      }
    } catch (error) {
      console.error("Refresh failed", error);
    } finally {
      setLoadingStations(false);
    }
  };

  const handleTouchEnd = async () => {
    // 1. Pull-to-Refresh Logic
    if (pullToRefreshDist > 70) {
      await handleRefresh();
    }
    setPullToRefreshDist(0);
    touchStartRef.current = 0;

    // 2. Swipe Nav Logic
    swipeHandlers.onTouchEnd();
  };

  const handleStationSelect = (station: Station) => {
    console.log("[App] handleStationSelect:", {
      id: station.id,
      name: station.name,
      provider: station.provider,
    });

    // Auto-minimize player on mobile when navigating to a station/podcast view
    console.log("[App] Auto-minimize check:", {
      isMobile,
      isPlayerExpanded,
      windowWidth: window.innerWidth,
    });
    if (isMobile && isPlayerExpanded) {
      console.log("[App] Minimizing player bar");
      setIsPlayerExpanded(false);
    }

    // Build a context list from currently displayed stations if we can
    const contextList = displayedStations || [];
    const idx = contextList.findIndex((s) => s.id === station.id);
    if (idx !== -1) {
      setCurrentContext({
        type: layoutMode === "carousel" ? "carousel" : "grid",
        items: contextList,
        index: idx,
      });
    }

    if (station.provider === "podcast" || station.id.startsWith("pod-")) {
      console.log("[App] Opening podcast view");
      handleViewChange("podcast-details", station.id);
    } else {
      console.log("[App] Opening radio view");
      if (currentStation?.id === station.id) {
        togglePlay();
      } else {
        setCurrentStation(station);
        const slug = makeStationSlug(station);
        handleViewChange("station", slug);
      }
    }
  };

  const handleGoHome = async () => {
    // UI Resets
    navigate("/");
    setActiveType("radio");
    setSelectedCountry("All");
    setLastRadioCountry("All");
    setSelectedTheme("");
    setLastRadioCategory("");
    setTypedSearchQuery("");
    setConfirmedSearchQuery("");
    setLayoutMode("carousel"); // Reset to default view
    setSortBy("popular");

    // Soft reset: Fetch top stations for the grid/list view backing data
    setLoadingStations(true);
    try {
      const tops = await getTopStations();
      setStations(tops);
    } catch (error) {
      console.warn("Failed to reset stations", error);
    } finally {
      setLoadingStations(false);
    }
  };

  const handleStationRequest = async (
    criteria: { genre?: string; country?: string; mood?: string },
    stayInCurrentView = false,
    isSurpriseMode = false,
  ) => {
    // Fun messages for Surprise Me mode
    const surpriseMessages = [
      "Surprising you...",
      "Searching for wonders...",
      "Dialing in the magic...",
      "Tuning into something special...",
      "Finding your next favorite...",
      "Spinning the cosmic dial...",
      "Scanning the airwaves...",
      "Discovering hidden gems...",
    ];

    const requestText = isSurpriseMode
      ? surpriseMessages[Math.floor(Math.random() * surpriseMessages.length)]
      : `Tuning to ${criteria.genre || criteria.mood || "something"} ${criteria.country ? `in ${criteria.country}` : ""}...`;

    setChatHistory((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        role: "model",
        text: requestText,
        timestamp: Date.now(),
      },
    ]);

    setLoadingStations(true);
    if (!stayInCurrentView) handleViewChange("search");

    const tag = criteria.genre || criteria.mood || "";
    const country =
      criteria.country ||
      (selectedCountry !== "All" ? selectedCountry : undefined);

    let results: Station[] = [];

    if (activeType === "pods") {
      // Search Podcasts
      const query = tag || criteria.genre || "podcast";
      // Import dynamically to avoid circular dependency issues if any, or just use the imported one
      const { searchPodcastsAsStations } =
        await import("./services/podcastService");
      results = await searchPodcastsAsStations(query, country);
    } else {
      // Search Radio
      results = await searchStations({ tag, country, limit: 20 });
    }

    if (results.length > 0) {
      setStations(results);
      // If the user asks for something specific, switch to grid to show results clearly
      setLayoutMode("grid");
      if (country) setSelectedCountry(country);
      // Pick a truly random station from ALL results, not just the first 3
      const randomIdx = Math.floor(Math.random() * results.length);
      const selectedStation = results[randomIdx];
      setCurrentStation(selectedStation);

      // Update the chat message with the actual station name
      setChatHistory((prev) => {
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.text === requestText) {
          lastMsg.text = `Tuning to ${selectedStation.name}...`;
        }
        return updated;
      });
    } else {
      setChatHistory((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: "model",
          text: "Couldn't find that exact frequency.",
          timestamp: Date.now(),
        },
      ]);
    }
    setLoadingStations(false);
  };

  const handleNavRadio = async () => {
    const nextCategory = lastRadioCategory || "";
    const nextCountry = lastRadioCountry || "All";
    setActiveType("radio");
    setSelectedTheme(nextCategory);
    setSelectedCountry(nextCountry);
    setSortBy("popular");
    navigate("/radio");

    if (layoutMode !== "carousel") {
      setLoadingStations(true);
      try {
        await loadFilteredStations("radio", nextCategory, nextCountry);
      } catch (error) {
        console.warn("Failed to load radio list for navigation", error);
      } finally {
        setLoadingStations(false);
      }
    }
  };

  const handleNavPodcasts = async () => {
    const nextCategory = lastPodcastCategory || "";
    const nextCountry = lastPodcastCountry || selectedCountry || "All";
    setActiveType("pods");
    setSelectedTheme(nextCategory);
    setSelectedCountry(nextCountry);
    setSortBy("popular");
    setLayoutMode("carousel"); // default to discovery rows for podcasts
    navigate("/podcasts");

    setLoadingStations(true);
    try {
      await loadFilteredStations("pods", nextCategory, nextCountry);
    } catch (error) {
      console.warn("Failed to load podcast list for navigation", error);
    } finally {
      setLoadingStations(false);
    }
  };

  const handleNavCreators = () => {
    navigate("/creators-choice");
  };

  const handleSurpriseMe = () => {
    const randomGenre =
      SURPRISE_GENRES[Math.floor(Math.random() * SURPRISE_GENRES.length)];
    setChatHistory((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        role: "user",
        text: "DJ Eater, surprise me!",
        timestamp: Date.now(),
      },
    ]);
    setHasSurprised(true);
    handleStationRequest({ genre: randomGenre }, true, true); // Pass true for surprise mode
  };

  const handleTypeChange = async (
    type: "radio" | "pods" | "curator" | "favs",
  ) => {
    setActiveType(type);
    setSelectedTheme("");

    // If switching type, default to carousel if in Radio/Pods mode
    if (type === "radio" || type === "pods") setLayoutMode("carousel");
    else setLayoutMode("grid");

    if (type === "favs") {
      navigate("/library");
    } else {
      if (viewMode === "library") handleViewChange("home");
      if (type === "pods")
        await loadFilteredStations("pods", "", selectedCountry);
      else if (type === "curator") {
        await loadFilteredStations("curator", "chill", selectedCountry);
        setSelectedTheme("chill");
      } else {
        // Radio mode doesn't need to load flat stations if in carousel mode
        if (layoutMode !== "carousel")
          await loadFilteredStations("radio", "", selectedCountry);
      }
    }
  };

  const handleCountryChange = async (country: string) => {
    setSelectedCountry(country);
    setTypedSearchQuery("");
    setStations([]);
    if (activeType === "radio") {
      setLastRadioCountry(country);
    } else if (activeType === "pods") {
      setLastPodcastCountry(country);
    }

    // If in flat list mode, reload list.
    // If in carousel mode, HomeView handles its own fetching based on the country prop
    if (layoutMode !== "carousel") {
      await loadFilteredStations(activeType, selectedTheme, country);
    }
  };

  const handleThemeSelect = async (val: string) => {
    console.log(
      "[App] handleThemeSelect called with:",
      val,
      "activeType:",
      activeType,
      "country:",
      selectedCountry,
    );
    setSelectedTheme(val);
    setTypedSearchQuery("");
    if (activeType === "radio") {
      setLastRadioCategory(val);
    } else if (activeType === "pods") {
      setLastPodcastCategory(val);
    }

    // Radios go to grid for clarity; pods/curator stay in carousel to show curated rows
    if (activeType === "radio") {
      setLayoutMode("grid");
    } else {
      setLayoutMode("carousel");
      handleViewChange("home");
    }

    console.log("[App] About to call loadFilteredStations with tag:", val);
    await loadFilteredStations(activeType, val, selectedCountry);
  };

  // AI Connection
  const {
    connect,
    disconnect,
    isConnected,
    mode: djMode,
    volumeLevel,
  } = useLiveDJ({
    onStationChangeRequest: (criteria) => handleStationRequest(criteria, false),
    persona: djPersona,
    weatherContext,
    locationContext,
  });

  const handleTextChat = async (e: React.FormEvent, overrideText?: string) => {
    e.preventDefault();
    const userMsg = overrideText || textInput;
    if (!userMsg.trim()) return;

    setTextInput("");
    setChatHistory((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        role: "user",
        text: userMsg,
        timestamp: Date.now(),
      },
    ]);

    const { text, toolCall } = await chatWithDJ(userMsg, djPersona);
    setChatHistory((prev) => [
      ...prev,
      {
        id: (Date.now() + 1).toString(),
        role: "model",
        text: text,
        timestamp: Date.now(),
      },
    ]);

    if (toolCall && toolCall.name === "tuneStation")
      await handleStationRequest(toolCall.args);

    const audioBuffer = await generateSpeech(text, djPersona);
    if (audioBuffer) {
      const ctx = new (window.AudioContext ||
        (window as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext)!();
      const buffer = await ctx.decodeAudioData(audioBuffer);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    }
  };

  const handleArtistInfo = async () => {
    if (!currentStation) return;
    if (!isSidebarOpen) setIsSidebarOpen(true);
    const prompt = `Who is the artist playing on ${currentStation.name} right now? Or what kind of music is typical for this station? Give me some interesting facts.`;
    setTextInput(prompt);
    handleTextChat({ preventDefault: () => {} } as React.FormEvent, prompt);
  };

  // Filtering for display
  const getDisplayStations = () => {
    let displayList = [];
    if (viewMode === "favorites") displayList = favorites;
    else displayList = stations;

    displayList = displayList.filter((s) => !hiddenStationIds.includes(s.id));

    return [...displayList].sort((a, b) => {
      switch (sortBy) {
        case "popular":
          return (b.votes || 0) - (a.votes || 0);
        case "trending":
          return (b.clickTrend || 0) - (a.clickTrend || 0);
        case "bitrate":
          return b.bitrate - a.bitrate;
        case "name":
          return a.name.localeCompare(b.name);
        case "latest_episode": {
          const dateA = new Date(a.lastEpisodeDate || 0).getTime();
          const dateB = new Date(b.lastEpisodeDate || 0).getTime();
          return dateB - dateA;
        }
        default:
          return 0;
      }
    });
  };

  const displayedStations = getDisplayStations();

  const handleSearchResultClick = (station: Station) => {
    if (station.provider === "podcast" || station.id.startsWith("pod-")) {
      // Cast to Podcast as we know it's a podcast from the provider check
      // and the search result structure for podcasts is compatible enough for the view
      handleViewChange("podcast-details", station.id);
      setIsSearchOpen(false);
      setTypedSearchQuery(""); // Clear search
    } else {
      const resultsContext = liveSearchResults || [];
      const idx = resultsContext.findIndex((s) => s.id === station.id);
      if (idx !== -1) {
        setCurrentContext({
          type: "search",
          items: resultsContext,
          index: idx,
        });
      }

      if (currentStation?.id === station.id) {
        if (!isPlaying) togglePlay();
      } else {
        setCurrentStation(station);
        // NEW: Open Radio Detail View on click
        handleViewChange("station", makeStationSlug(station));
      }
      // NEW: Do NOT close search for radio stations, to allow continued browsing
      // setIsSearchOpen(false);
      // setStationSearchQuery(''); // Clear search
    }
  };

  // --- CENTRALIZED PLAY HANDLER to avoid double-playback issues ---
  const handleGlobalPlayStation = (s: Station) => {
    triggerGate(() => {
      // CRITICAL: Check BOTH id AND streamUrl
      // Podcast episodes share the same podcast ID but have different streamUrls
      const isSameStation =
        currentStation?.id === s.id &&
        currentStation?.streamUrl === s.streamUrl;
      if (isSameStation) {
        handleGlobalTogglePlay();
      } else {
        setCurrentStation(s);
        // Reset Creator State
        setCreatorTrack(null);
        setCreatorCurrentTime(0);

        if (s.provider === "creator" || s.provider === "custom") {
          setIsCreatorPlaying(true);
          setIsPlaying(false); // Stop standard audio
        } else {
          setIsPlaying(true);
          setIsCreatorPlaying(false); // Stop creator audio
        }
      }
    });
  };

  const contextValue = {
    currentStation,
    isPlaying,
    isBuffering,
    playStation: handleGlobalPlayStation,
    togglePlay: () => {
      // For togglePlay, we might also want to gate, but maybe less intrusive if just pausing?
      // If playing -> pausing: No gate.
      // If paused -> playing: Gate?
      if (!isPlaying) {
        triggerGate(() => togglePlay());
      } else {
        togglePlay();
      }
    },
    favorites,
    toggleFavorite,
    activeType,
    setActiveType: handleTypeChange,
    viewMode,
    setViewMode: (mode: ViewMode) => handleViewChange(mode),
    handleStationSelect,
    stations,
    setStations,
    loadingStations,
    selectedCountry,
    setSelectedCountry: handleCountryChange,
    handleStationRequest,
    currentContext,
    setCurrentContext,
    triggerGate, // Expose for LandingPage
    creatorTrack,
    isCreatorPlaying,
    handleSeek,
    currentTime,
    onOpenProfile: () => setIsProfileOpen(true),
  };

  return (
    // APP CONTAINER: Fixed Height, Flex Column, Hidden Overflow to behave like a native app
    <AppContext.Provider value={contextValue}>
      <div
        className={`h-[100dvh] w-full flex flex-col relative bg-radio-900 overflow-hidden transition-colors duration-500 theme-${themeMode}`}
      >
        {/* Creator Audio Engine */}
        {isCreatorMode && currentStation && (
          <CreatorAudioEngine
            channelId={currentStation.id}
            isPlaying={isCreatorPlaying}
            volume={volume}
            isMuted={volume === 0}
            onTrackChange={(track) => {
              setCreatorTrack(track);
            }}
            onTimeUpdate={(curr, dur) => {
              setCreatorCurrentTime(curr);
              setCreatorDuration(dur);
            }}
            onError={(err) => setLastError(err)}
            onPlayStateChange={setIsCreatorPlaying}
          />
        )}

        {/* Standard Audio Player - Forced pause if in Creator Mode */}
        <audio
          ref={audioRef}
          onError={(e) => {
            console.warn("Audio Error", e);
            if (
              audioRef.current &&
              audioRef.current.error &&
              audioRef.current.error.code !== 20
            ) {
              setIsPlaying(false);
            }
          }}
          onEnded={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
        />

        {isZenMode && currentStation && (
          <React.Suspense fallback={null}>
            <ZenMode
              station={currentStation}
              onClose={() => setIsZenMode(false)}
              isPlaying={isPlaying}
              onTogglePlay={togglePlay}
              sleepTimer={sleepTimer}
              setSleepTimer={setSleepTimer}
            />
          </React.Suspense>
        )}

        {/* HEADER is now inside scroll container below */}

        {/* SIDEBAR (Chat) */}
        <React.Suspense fallback={null}>
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            chatHistory={chatHistory}
            onSendMessage={handleTextChat}
            isDjMode={djMode !== "standard"}
            isConnected={isConnected}
            onToggleDj={() => (isConnected ? disconnect() : connect())}
            currentStation={currentStation}
            onArtistInfo={handleArtistInfo}
            textInput={textInput}
            setTextInput={setTextInput}
            djPersona={djPersona}
            setDjPersona={setDjPersona}
            isAutoAnnounceEnabled={isAutoAnnounceEnabled}
            setIsAutoAnnounceEnabled={setIsAutoAnnounceEnabled}
            sleepTimer={sleepTimer}
            setSleepTimer={setSleepTimer}
            weatherContext={weatherContext}
            onSurpriseMe={handleSurpriseMe}
            onTextChat={handleTextChat}
            volumeLevel={volumeLevel}
          />
        </React.Suspense>

        <React.Suspense fallback={null}>
          <ProfileMenu
            isOpen={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
            onNavigate={handleViewChange}
            themeMode={themeMode}
            setThemeMode={setThemeMode}
            setAccentColor={setAccentColor}
            triggerRef={profileTriggerRef}
            user={user}
            isMobile={isMobile}
            isPlaying={isPlaying}
            onCustomize={() => setCustomizeSection("radio")}
            onZenMode={handleOpenZen}
          />
        </React.Suspense>

        {/* Mobile Search View - Fullscreen */}
        {isMobile && (
          <React.Suspense fallback={null}>
            <MobileSearchView
              isOpen={isSearchOpen}
              onClose={() => setIsSearchOpen(false)}
              searchQuery={typedSearchQuery}
              setSearchQuery={setTypedSearchQuery}
              searchResults={liveSearchResults}
              onSearchResultClick={(station) => {
                if (station.provider === "podcast" || station.id.startsWith("pod-")) {
                  navigate(`/podcasts/${station.id}`);
                } else {
                  navigate(`/radio/${makeStationSlug(station)}`);
                }
                setIsSearchOpen(false);
              }}
              onManualSearch={handleManualSearch}
              activeType={activeType}
              onThemeSelect={(theme) => {
                handleThemeSelect(theme);
                // Ensure filter sheet is closed if we're selecting via search
                setIsFilterBottomSheetOpen(false);
              }}
            />
          </React.Suspense>
        )}

        {/* Mobile Filter Bottom Sheet */}
        {isMobile && (
          <React.Suspense fallback={null}>
            <FilterBottomSheet
              isOpen={isFilterBottomSheetOpen}
              onClose={() => setIsFilterBottomSheetOpen(false)}
              selectedCountry={selectedCountry}
              onCountryChange={handleCountryChange}
              countries={countries}
              sortBy={sortBy}
              setSortBy={setSortBy}
              layoutMode={layoutMode}
              onLayoutModeChange={setLayoutMode}
              activeType={activeType}
              selectedTheme={selectedTheme}
              onThemeSelect={handleThemeSelect}
            />
          </React.Suspense>
        )}

        {/* HEADER - Fixed on mobile, relative on desktop */}
        <div
          className={`
                        ${isSearchOpen || isFilterOpen ? "hidden md:block" : ""}
                        w-full z-[90]
                        ${isMobile ? "fixed top-0 left-0 right-0" : "relative block flex-shrink-0"}
                    `}
        >
          <Header
            themeMode={themeMode}
            isPlaying={isPlaying}
            activeNav={activeNav}
            onNavigateStart={handleGoHome}
            onNavigateRadio={handleNavRadio}
            onNavigatePodcasts={handleNavPodcasts}
            onNavigateCreators={handleNavCreators}
            onGoToFavorites={() => navigate("/library")}
            onGoToHistory={() => navigate("/library")}
            onToggleProfile={toggleProfilePanel}
            onGoHome={handleGoHome}
            profileTriggerRef={profileTriggerRef}
            onSearchClick={toggleSearchOverlay}
            isMobileMenuOpen={isMainMenuOpen}
            onOpenMobileMenu={openMainMenu}
            onCloseMobileMenu={closeMainMenu}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAllRead={markAllRead}
            onClearNotifications={clearNotifications}
            onRemoveNotification={removeNotification}
          />
        </div>

        {/* MAIN CONTENT: Flex-1 (Takes remaining space), Scrollable */}
        <div
          ref={scrollContainerRef} // Use local ref
          className="flex-1 overflow-y-auto custom-scrollbar overscroll-contain relative z-0"
          // Touch
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          // Mouse (Desktop Swipe)
          onMouseDown={swipeHandlers.onMouseDown}
          onMouseMove={swipeHandlers.onMouseMove}
          onMouseUp={swipeHandlers.onMouseUp}
          onMouseLeave={swipeHandlers.onMouseLeave}
          style={{
            paddingBottom: currentStation
              ? isPlayerExpanded
                ? "0px"
                : "calc(80px + env(safe-area-inset-bottom))"
              : "env(safe-area-inset-bottom)",
          }}
        >
          {/* Modern "Pill" Pull to Refresh Indicator */}
          <div
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex flex-col items-center justify-center transition-all duration-200 will-change-transform"
            style={{
              opacity: Math.min(1, Math.max(0, (pullToRefreshDist - 20) / 40)),
              transform: `translate(-50%, ${pullToRefreshDist > 0 ? pullToRefreshDist / 3 : 0}px)`,
            }}
          >
            <div
              className={`
                            flex items-center gap-2 px-4 py-2 rounded-full shadow-xl backdrop-blur-xl border border-white/10
                            transition-all duration-300
                            ${
                              pullToRefreshDist > 70
                                ? "bg-white text-black scale-110"
                                : "bg-black/60 text-white scale-100"
                            }
                        `}
            >
              <div
                className={`w-4 h-4 rounded-full border-2 border-current border-t-transparent ${pullToRefreshDist > 70 ? "animate-spin" : ""}`}
              />
              <span className="text-xs font-bold tracking-wide">
                {pullToRefreshDist > 70 ? "Release" : "Pull"}
              </span>
            </div>
          </div>

          {/* SPACER for Mobile Fixed Header */}
          <div className="h-[64px] w-full md:hidden flex-shrink-0" />

          <div className="w-full pt-0 md:pt-5 px-4 md:px-6 lg:px-8">
            {/* pt-0 on mobile because we have spacer, md:pt-5 for desktop gap */}
            {((viewMode !== "podcast-details" && viewMode !== "station") ||
              isSearchOpen) && (
              // --- FILTER BAR - Sticky at top for easy access ---
              // Mobile: Top varies based on Header visibility
              // Desktop: Top 0
              <div
                className="sticky z-50 -mx-4 px-4 md:mx-0 md:px-0 pt-1 pb-0 glass-sticky transition-all duration-300 overflow-visible"
                style={{
                  zIndex: 90,
                  pointerEvents: "auto",
                  top: isMobile ? `${HEADER_HEIGHT_MOBILE}px` : "0px",
                }}
              >
                <div className="w-full overflow-visible">
                  <FilterBar
                    activeType={activeType}
                    onTypeChange={handleTypeChange}
                    viewMode={viewMode}
                    selectedTheme={selectedTheme}
                    onThemeSelect={handleThemeSelect}
                    selectedCountry={selectedCountry}
                    onCountryChange={handleCountryChange}
                    countries={countries}
                    onNearMe={() =>
                      handleNearMe(setWeatherContext, setLocationContext)
                    }
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    stationSearchQuery={typedSearchQuery}
                    setStationSearchQuery={setTypedSearchQuery}
                    onManualSearch={handleManualSearch}
                    searchResults={liveSearchResults}
                    onSearchResultClick={(station) => {
                      handleSearchResultClick(station);
                    }}
                    setViewMode={handleViewChange}
                    userRegion={userRegion}
                    podcastCounts={podcastCounts}
                    isSearchOpen={isSearchOpen}
                    setIsSearchOpen={setIsSearchOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    favorites={favorites}
                    onToggleFavorite={toggleFavorite}
                    setIsFilterBottomSheetOpen={setIsFilterBottomSheetOpen}
                    layoutMode={layoutMode}
                    onLayoutModeChange={setLayoutMode}
                    onToggleCustomize={() =>
                      setCustomizeSection((prev) =>
                        prev
                          ? null
                          : activeType === "pods"
                            ? "podcasts"
                            : "radio",
                      )
                    }
                  />

                  <div className="w-full max-w-[280px] h-6 -mt-3 opacity-100 md:hidden mx-auto pointer-events-none">
                    <HeaderOscilloscope
                      isPlaying={isPlaying}
                      stationId={currentStation?.id}
                    />
                  </div>
                </div>
              </div>
            )}

            {locationError && (
              <div className="mt-4 mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center gap-2 animate-[fadeIn_0.3s_ease-out] justify-center">
                <AlertCircle />
                {locationError}
              </div>
            )}

            {/* Main Content - Animation Target for Swipe Transitions */}
            <div id="main-content" className="transition-transform">
              <Routes>
                <Route path="/" element={<FrontDoor />} />
                <Route
                  path="/radio"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <HomeRoute
                        selectedCountry={selectedCountry}
                        layoutMode={layoutMode}
                        onLayoutModeChange={setLayoutMode}
                        stations={displayedStations}
                        onNavigateCreators={() => navigate("/creators-choice")}
                        onSetContext={(items, index, type) =>
                          setCurrentContext({ type, items, index })
                        }
                        user={user}
                        userRegion={userRegion}
                        customizeSection={customizeSection}
                        setCustomizeSection={setCustomizeSection}
                      />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/podcasts"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <HomeRoute
                        selectedCountry={selectedCountry}
                        layoutMode={layoutMode}
                        onLayoutModeChange={setLayoutMode}
                        stations={displayedStations}
                        onNavigateCreators={() => navigate("/creators-choice")}
                        onSetContext={(items, index, type) =>
                          setCurrentContext({ type, items, index })
                        }
                        user={user}
                        userRegion={userRegion}
                        customizeSection={customizeSection}
                        setCustomizeSection={setCustomizeSection}
                      />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/library"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <LibraryRoute />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <SearchRoute />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/radio/moods/:moodId"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <MoodView />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/radio/:slug"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <RadioRoute />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/podcasts/:id"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <PodcastRoute />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/pods"
                  element={<Navigate to="/podcasts" replace />}
                />
                <Route
                  path="/pods/:id"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <PodcastRoute />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/creators-choice"
                  element={<CreatorsChoicePage />}
                />
                <Route
                  path="/creators-choice/:channelId"
                  element={
                    <React.Suspense
                      fallback={<div className="min-h-screen bg-black" />}
                    >
                      <LiveStationView />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/login"
                  element={<LoginView onNavigate={handleViewChange} />}
                />
                <Route
                  path="/register"
                  element={<RegisterView onNavigate={handleViewChange} />}
                />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route
                  path="/account"
                  element={<AccountView onNavigate={handleViewChange} />}
                />
                <Route
                  path="/api-docs"
                  element={<ApiDocs onBack={() => navigate("/")} />}
                />
                <Route path="/legal/:slug" element={<LegalView />} />
                {/* Legal */}
                <Route path="/legal/*" element={<LegalView />} />

                {/* 404 Catch-All */}
                <Route path="*" element={<NotFoundView />} />
              </Routes>
            </div>

            {/* Footer - Show on main views except detail pages */}
            {viewMode !== "podcast-details" &&
              viewMode !== "station" &&
              viewMode !== "library" &&
              viewMode !== "search" &&
              viewMode !== "landing" && <Footer />}
            {/* Global Bottom Spacer for PlayerBar */}
            <div className="h-28 md:h-32 w-full shrink-0"></div>
          </div>
        </div>

        <div
          className={`transition-all duration-300 ease-in-out transform ${suppressMiniPlayer || !currentStation ? "translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"}`}
        >
          <PlayerBar
            currentStation={effectiveCurrentStation}
            isPlaying={effectiveIsPlaying}
            isExpanded={isPlayerExpanded}
            setIsExpanded={setIsPlayerExpanded}
            showEq={showEq}
            setShowEq={setShowEq}
            eqGains={eqGains}
            onEqGainChange={(idx, val) => {
              const n = [...eqGains];
              n[idx] = val;
              setEqGains(n);
            }}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
            onTogglePlay={handleGlobalTogglePlay}
            volume={volume}
            setVolume={setVolume}
            onArtistInfo={handleArtistInfo}
            onZenMode={handleOpenZen}
            isSidebarOpen={isSidebarOpen}
            currentTime={effectiveCurrentTime}
            duration={effectiveDuration}
            onSeek={isCreatorMode ? undefined : handleSeek} // Disable seeking for Live Creator Radio for now
            onSkip={isCreatorMode ? undefined : handleSkip}
            onNext={() => handleSkipStation("next")}
            onPrev={() => handleSkipStation("prev")}
            onShuffle={handleShuffle}
            isShuffling={isShuffling}
            playbackSpeed={playbackSpeed}
            onCyclePlaybackSpeed={cyclePlaybackSpeed}
            onToggleSidebar={() => setIsSidebarOpen(true)}
            onOpenDiscover={() => handleViewChange("home")}
            contextInfo={
              currentContext && currentStation
                ? {
                    type: currentContext.type,
                    currentIndex: Math.max(
                      1,
                      currentContext.items.findIndex(
                        (s) => s.id === currentStation.id,
                      ) + 1,
                    ),
                    totalItems: currentContext.items.length,
                  }
                : null
            }
            onNavigateToStation={() => {
              if (effectiveCurrentStation) {
                if (
                  effectiveCurrentStation.provider === "podcast" ||
                  effectiveCurrentStation.id.startsWith("pod-")
                ) {
                  handleViewChange(
                    "podcast-details",
                    effectiveCurrentStation.id,
                  );
                } else {
                  const slug = makeStationSlug(effectiveCurrentStation);
                  handleViewChange("station", slug);
                }
              }
            }}
          />
        </div>

        <Toast
          message={lastError}
          type="error"
          onDismiss={() => setLastError(null)}
        />

        <React.Suspense fallback={null}>
          <AuthGate
            isOpen={isGateOpen}
            onClose={handleGateClose}
            onContinueGuest={handleGateGuest}
          />
        </React.Suspense>
        <SpeedInsights />

        {/* Cookie Consent Banner */}
        <React.Suspense fallback={null}>
          <CookieConsent />
        </React.Suspense>

        {/* Keyboard Shortcuts Modal */}
        <HotkeyMenu isOpen={isHotkeyMenuOpen} onClose={closeHotkeyMenu} />

        {/* Scroll Progress Indicator */}
        <ScrollProgress />

        {/* Now Playing Toast */}
        <NowPlayingToast
          stationName={currentStation?.name || ""}
          artistName={currentStation?.country}
          logoUrl={currentStation?.logoUrl}
          isVisible={showNowPlayingToast}
        />
      </div>
    </AppContext.Provider>
  );
}

export default App;
